import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Loader2, UserPlus, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InviteSignupPage = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { registerFromInvite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [existingUserName, setExistingUserName] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await axios.get(`${API}/auth/invite/${inviteId}`);
        setInvite(response.data);
        setFormData(prev => ({ ...prev, email: response.data.email }));
        
        // Check if user already exists
        if (response.data.user_exists) {
          setUserExists(true);
          setExistingUserName(response.data.existing_user_name || '');
          setShowLoginForm(true);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invite');
      }
      setLoading(false);
    };
    fetchInvite();
  }, [inviteId]);

  // Handle registration for new users
  const handleRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerFromInvite(inviteId, formData.email, formData.password, formData.full_name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setSubmitting(false);
  };

  // Handle login for existing users - adds athlete to their account
  const handleExistingUserLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/accept-invite-existing`, null, {
        params: { 
          invite_id: inviteId, 
          email: formData.email, 
          password: formData.password 
        }
      });
      
      // Store the token and update auth state
      localStorage.setItem('meybra_token', response.data.token);
      
      toast.success(response.data.message || `${invite?.athlete_name} added to your account!`);
      
      // Redirect to parent dashboard - force refresh to update auth state
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl uppercase mb-4 text-red-400">Invalid Invite</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-primary text-black">Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="absolute inset-0 green-mesh opacity-20" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-2 mb-8 justify-center">
          {invite?.branding?.logo_url ? (
            <img src={invite.branding.logo_url} alt="Logo" className="h-10 w-auto" />
          ) : (
            <Zap className="text-primary" size={32} />
          )}
          <span className="font-heading text-2xl">{invite?.coach_business_name || 'MEYBRA'}</span>
        </div>
        
        <div className="bg-card border border-border/40 p-8 rounded-sm">
          <div className="text-center mb-6">
            <h2 className="font-heading text-2xl uppercase mb-2">
              {userExists ? 'Welcome Back!' : 'Welcome!'}
            </h2>
            <p className="text-muted-foreground">
              {userExists ? (
                <>
                  Hi <span className="text-primary font-semibold">{existingUserName}</span>! 
                  You've been invited to add{' '}
                  <span className="text-primary font-semibold">{invite?.athlete_name}</span>{' '}
                  to your account.
                </>
              ) : (
                <>
                  You've been invited to join as a parent for{' '}
                  <span className="text-primary font-semibold">{invite?.athlete_name}</span>
                </>
              )}
            </p>
          </div>

          <div className="bg-muted/30 p-4 rounded-sm mb-6">
            <p className="text-sm text-muted-foreground mb-2">Athlete Details</p>
            <p className="font-semibold">{invite?.athlete_name}</p>
            <p className="text-sm text-muted-foreground">
              Age: {invite?.athlete_age} | Position: {invite?.athlete_position}
            </p>
          </div>

          {/* Existing User - Login Form */}
          {showLoginForm && userExists ? (
            <form onSubmit={handleExistingUserLogin} className="space-y-5">
              <div className="bg-primary/10 border border-primary/30 p-4 rounded-sm mb-4">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <LogIn size={18} />
                  <span className="font-semibold text-sm uppercase">Account Found</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  We found an existing account with this email. Sign in to add this athlete to your profile.
                </p>
              </div>
              
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-muted/50 border-border cursor-not-allowed"
                />
              </div>
              
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Password</label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  className="bg-transparent border-border"
                  placeholder="Enter your password"
                  data-testid="invite-login-password"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={submitting} 
                className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider py-6"
                data-testid="invite-login-submit"
              >
                {submitting ? 'Adding Athlete...' : `Add ${invite?.athlete_name} to My Account`}
              </Button>
              
              <div className="text-center">
                <button 
                  type="button"
                  onClick={() => setShowLoginForm(false)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Not your account? Create a new one
                </button>
              </div>
            </form>
          ) : (
            /* New User - Registration Form */
            <form onSubmit={handleRegister} className="space-y-5">
              {userExists && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-sm mb-4">
                  <div className="flex items-center gap-2 text-yellow-500 mb-2">
                    <UserPlus size={18} />
                    <span className="font-semibold text-sm uppercase">Creating New Account</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Note: An account with this email already exists. Creating a new account will use a different email.
                  </p>
                </div>
              )}
              
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Your Name</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                  className="bg-transparent border-border"
                  placeholder="Your full name"
                  data-testid="invite-register-name"
                />
              </div>
              
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-transparent border-border"
                  placeholder="your@email.com"
                  data-testid="invite-register-email"
                />
              </div>
              
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Password</label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  className="bg-transparent border-border"
                  placeholder="Create a password"
                  data-testid="invite-register-password"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={submitting} 
                className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider py-6"
                data-testid="invite-register-submit"
              >
                {submitting ? 'Creating Account...' : 'Create Account'}
              </Button>
              
              {userExists && (
                <div className="text-center">
                  <button 
                    type="button"
                    onClick={() => setShowLoginForm(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    Already have an account? Sign in instead
                  </button>
                </div>
              )}
            </form>
          )}
          
          {!userExists && (
            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">Sign In</Link>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InviteSignupPage;
